<template>
  <HeadTitle
    title="Empleados"
    description="lista de empleados de una compañia seleccionada"
  />
  <DataTable
    :value="employees"
    dataKey="id"
    v-model:filters="filters"
    :loading="isLoading"
    filter-display="row"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    tableStyle="min-width: 60rem"
    currentPageReportTemplate="{first} a {last} de {totalRecords}"
    class="tabla mb-3"
    paginator
    :rows="10"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    @filter="onFilter($event)"
    :filteredValue="filteredEmployees"
    :globalFilterFields="[
      'code',
      'name',
      'identificationNumber',
      'nationality',
      'email',
      'position',
      'supervisor.fullname',
    ]"
  >
    <template #header>
      <div class="flex justify-content-end">
        <Button
          icon="pi pi-users"
          label="Crear usuarios"
          @click="isCreateUsersDialogOpen = !isCreateUsersDialogOpen"
          class="mr-3 text-sm font-light"
        ></Button>
        <Button
          icon="pi pi-file-excel"
          label="Exportar"
          @click="exportCSV()"
          class="text-sm font-light bg-green-100 border-green-100 text-green-700 mr-3"
        />
        <span class="p-input-icon-left">
          <i class="pi pi-search" />
          <InputText v-model="filters['global'].value" placeholder="Buscar" />
        </span>
      </div>
    </template>
    <Column
      field="payroll.code"
      header="Código Nómina"
      style="width: 100px"
      sortable
      filter
    >
      <template #body="{ data }">
        <span>{{ data.payroll.code }}</span>
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="payrollsCode"
          placeholder=""
          class="p-column-filter"
          style="min-width: 10rem"
          :maxSelectedLabels="2"
        >
          <template #option="slotProps">
            <div class="flex align-items-center gap-2">
              <span>{{ slotProps.option }}</span>
            </div>
          </template>
        </MultiSelect>
      </template>
    </Column>
    <Column field="code" header="Código empleado" sortable></Column>
    <Column
      field="identificationNumber"
      header="Numero identificación"
      sortable
    ></Column>
    <Column field="name" header="Nombre" sortable></Column>
    <Column field="nationality" header="Nacionalidad" sortable></Column>
    <Column field="email" header="Correo" sortable></Column>
    <Column field="position" header="Cargo" sortable></Column>
    <Column field="salaryAmount" header="Salario" sortable></Column>
    <Column field="currency" header="Moneda" sortable></Column>
    <Column field="salaryBase" header="Base salarial" sortable></Column>
    <Column
      field="vacationBalance"
      header="Saldo de vacaciones"
      sortable
    ></Column>
    <Column field="isInactive" header="Estado" sortable>
      <template #body="{ data, field }">
        <span v-if="data[field]">Inactivo</span>
        <span v-else>Activo</span>
      </template>
    </Column>
    <Column field="joiningDate" header="Fecha de alta" sortable>
      <template #body="{ data, field }">
        {{ formatDate(data[field]) }}
      </template>
    </Column>
    <Column field="leaveDate" header="Fecha de baja" sortable>
      <template #body="{ data, field }">
        <span v-if="data[field]">{{ formatDate(data[field]) }}</span>
      </template>
    </Column>
    <Column field="userData.email" header="Correo de usuario"></Column>
    <Column field="userData.roleName" header="Rol de usuario">
      <template #body="{ data, field }">
        <span>{{ roleNames.get(data.userData?.roleName || '') }}</span>
      </template>
    </Column>
    <Column field="userData.userState" header="Estado de usuario">
      <template #body="{ data, field }">
        {{ TranslateUserStatus(data.userData?.userState) }}
      </template>
    </Column>
    <Column field="supervisor.fullname" header="Supervisor" sortable></Column>
    <Column
      field="supervisor.userData.email"
      header="Correo supervisor"
      sortable
    ></Column>
    <Column
      field="supervisor.userData.userState"
      header="Estado Supervisor"
      sortable
    ></Column>
  </DataTable>
  <Dialog
    v-model:visible="isCreateUsersDialogOpen"
    :style="{ width: '50vw' }"
    modal
    :breakpoints="{ '960px': '75vw', '641px': '100vw' }"
  >
    <template #header>
      <div class="flex flex-row">
        <h2>Crear usuarios</h2>
        <InfoButton
          class="ml-3"
          message="Selecciona los empleados a los cuales se les creara un usuario de Autogestión. Los empleados deberan tener un correo electronico asignado."
        />
        <h3 class="mt-1 ml-4" v-if="employeesToCreateUser?.length > 0">
          {{ employeesToCreateUser?.length }} empleados seleccionados
        </h3>
      </div>
    </template>
    <DataTable
      v-model:filters="filtersForDialog"
      style="width: 90%"
      scrollable
      scrollHeight="400px"
      v-model:selection="employeesToCreateUser"
      :value="availablesEmployeesToCreateUser"
      dataKey="code"
      tableStyle="min-width: 50rem"
      :globalFilterFields="['name', 'email', 'code', 'email']"
    >
      <template #header>
        <div class="flex justify-content-end">
          <span class="p-input-icon-left">
            <i class="pi pi-search" />
            <InputText
              v-model="filtersForDialog['global'].value"
              placeholder="Buscar"
            />
          </span>
        </div>
      </template>
      <template #empty> No se encontraron empleados disponibles. </template>
      <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
      <Column field="code" header="Código"></Column>
      <Column field="name" header="Nombre"></Column>
      <Column field="email" header="Correo"></Column>
    </DataTable>
    <template #footer>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        @click="isCreateUsersDialogOpen = false"
        text
      />
      <Button
        class="text-ligth text-sm"
        label="Crear usuarios"
        icon="pi pi-check"
        @click="createUsers"
        autofocus
        :disabled="!isValidToCreateUsers"
      />
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
  import { ref, onMounted, watch, computed } from 'vue'
  import useCompany from '@/store/company'
  import { storeToRefs } from 'pinia'
  import useEmployees from '@/shared/composables/useEmployees'
  import { FilterMatchMode } from 'primevue/api'
  import { formatDate } from '@/shared/utils/formatDate'
  import TranslateUserStatus from '@/shared/utils/translateUserStatus'
  import { EmployeeDetail } from '@/modules/employees/models/EmployeeDetail'
  import EmployeesReportXlsx from '@/shared/utils/reports/employeesReportXlsx'
  import roleNames from '@/shared/utils/rolesName'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import {
    createManyUsers,
    type CreateManyUsersResult,
  } from '@/modules/users/services/UserService'
  import CreateManyUsersDto from '@/modules/users/services/interfaces/CreateManyUsersDto'
  import HeadTitle from '@/shared/components/HeadTitle.vue'

  const isCreateUsersDialogOpen = ref(false)
  const companyStore = useCompany()

  const isLoading = ref(false)

  const { company } = storeToRefs(useCompany())

  const employees = ref()
  const employeesPivot = ref()
  const filteredEmployees = ref()
  const payrollsCode = ref()
  const employeesToCreateUser = ref()
  const availablesEmployeesToCreateUser = ref()

  watch(filteredEmployees, () => {
    availablesEmployeesToCreateUser.value = filteredEmployees.value.filter(
      (employee: EmployeeDetail) => employee.email && !employee.userData
    )
  })

  companyStore.$subscribe(async (_, state) => {
    company.value = state.company
    await refreshData()
  })

  onMounted(async () => {
    await refreshData()
  })

  const refreshData = async () => {
    if (company.value) {
      isLoading.value = true
      const employeesResponse = await useEmployees({
        requireUserData: true,
        companyId: company.value.id,
      })
      employees.value = employeesResponse
      filteredEmployees.value = employeesResponse
      employeesPivot.value = employeesResponse
      payrollsCode.value = employeesResponse.reduce(
        (acc: string[], employee: EmployeeDetail) => {
          if (!acc.includes(employee.payroll.code)) {
            acc.push(employee.payroll.code)
          }
          return acc
        },
        []
      )
      isLoading.value = false
    }
  }

  const createUsers = async () => {
    if (employeesToCreateUser.value.length > 0 && company.value) {
      isLoading.value = true
      try {
        let dto: CreateManyUsersDto = {
          clientId: company.value.client.id,
          companyId: company.value.id,
          userDetail: employeesToCreateUser.value.map(
            (employee: EmployeeDetail) => {
              return {
                employeeId: employee.id,
              }
            }
          ),
        }
        const result: CreateManyUsersResult = await createManyUsers(dto)
        console.log('RESULT', result)
        if (result.success) {
          isCreateUsersDialogOpen.value = false
          await refreshData()
        } else {
          const url = URL.createObjectURL(result.blob)
          const link = document.createElement('a')
          link.href = url
          link.download = result.fileName
          link.click()
          URL.revokeObjectURL(url)
        }
      } finally {
        isLoading.value = false
      }
    }
  }

  let typingTimer: any = null

  const onFilter = (event: any) => {
    clearTimeout(typingTimer)

    //add a delay when is typing
    typingTimer = setTimeout(() => {
      const { filteredValue } = event
      const filteredValueCasted = filteredValue as EmployeeDetail[]
      filteredEmployees.value =
        filteredValueCasted.length > 0 ? filteredValueCasted : employees.value
    }, 400)
  }

  const exportCSV = () => {
    if (!filteredEmployees.value) return
    EmployeesReportXlsx(filteredEmployees.value)
  }

  const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    'payroll.code': { value: null, matchMode: FilterMatchMode.IN },
  })

  const filtersForDialog = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })

  const isValidToCreateUsers = computed(() => {
    return employeesToCreateUser.value?.length > 0
  })
</script>
